import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { API_TARGET, apiRequest } from "@mhj/shared/api";
import { LG_NAMES } from "../lib/lga";
import { CATEGORY_LABELS, REQUIRED_DOCS } from "../lib/scholarship";
import { useAuth } from "@mhj/shared/auth";
import type { ScholarshipApplication, ScholarshipCategory } from "@mhj/shared/types";

type DraftForm = {
  category: ScholarshipCategory;
  lga: string;
  state: string;
  profileCode: string;
  jambEPin: string;
  bankName: string;
  bankAccountNumber: string;
  bankAccountName: string;
};

export default function ScholarshipApply() {
  const { user } = useAuth();
  const uploadSizeHint = "Max file size: JPG/PNG 300KB, PDF 2MB.";
  const queryClient = useQueryClient();
  const [application, setApplication] = useState<ScholarshipApplication | null>(
    null
  );
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DraftForm>({
    defaultValues: {
      category: "jamb",
      lga: "",
      state: "Niger",
      profileCode: "",
      jambEPin: "",
      bankName: "",
      bankAccountNumber: "",
      bankAccountName: "",
    },
  });
  const [category] = watch(["category"]);
  const { data: configData } = useQuery({
    queryKey: ["scholarship-config"],
    queryFn: () =>
      apiRequest<{ enabledCategories: string[]; allowedLgas: string[] | null }>(
        "/scholarships/config",
      ),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
  const enabledCategories = configData?.enabledCategories ?? [
    "jamb",
    "undergraduate",
    "postgraduate",
    "others",
  ];
  const allowedLgas =
    configData?.allowedLgas === null || configData?.allowedLgas === undefined
      ? LG_NAMES
      : configData.allowedLgas;

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ["scholarship", "me", user?.id ?? "anonymous"],
    enabled: !!user,
    queryFn: () =>
      apiRequest<{ application: ScholarshipApplication | null }>("/scholarships/me"),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  const requiredDocs = useMemo(() => REQUIRED_DOCS[category], [category]);
  const missingDocs = useMemo(() => {
    if (!application) return requiredDocs;
    const existing = new Set(application.documents.map((doc) => doc.docType));
    return requiredDocs.filter((doc) => !existing.has(doc));
  }, [application, requiredDocs]);
  const requiredUploadDocs = useMemo(
    () => requiredDocs.filter((doc) => doc !== "passport"),
    [requiredDocs]
  );
  const passportDocument = useMemo(
    () => application?.documents.find((doc) => doc.docType === "passport") ?? null,
    [application]
  );

  useEffect(() => {
    if (data?.application) {
      setApplication(data.application);
      setValue("category", data.application.category);
      setValue("lga", data.application.lga);
      setValue("state", data.application.state);
      setValue("profileCode", data.application.profileCode ?? "");
      setValue("jambEPin", data.application.jambEPin ?? "");
      setValue("bankName", data.application.bankName ?? "");
      setValue("bankAccountNumber", data.application.bankAccountNumber ?? "");
      setValue("bankAccountName", data.application.bankAccountName ?? "");
    } else {
      setApplication(null);
      if (enabledCategories.length > 0 && !enabledCategories.includes(category)) {
        setValue("category", enabledCategories[0] as ScholarshipCategory);
      }
    }
  }, [data, setValue, enabledCategories, category]);

  useEffect(() => {
    if (queryError) {
      toast.error((queryError as Error).message);
    }
  }, [queryError]);

  const handleStart = async (values: DraftForm) => {
    try {
      await startMutation.mutateAsync(values);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleUpload = async (docType: string, file: File | null) => {
    if (!file || !application) return;
    try {
      await uploadMutation.mutateAsync({ docType, file });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleSubmitApplication = async () => {
    if (!application) return;
    try {
      await submitMutation.mutateAsync({ applicationId: application.id });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handlePrintAcknowledgement = () => {
    if (!application?.submittedAt) return;

    const escapeHtml = (value: string) =>
      value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");

    const docsMarkup =
      application.documents.length === 0
        ? `<p class="muted">No documents found.</p>`
        : application.documents
            .map(
              (doc) => `
                <tr>
                  <td>${escapeHtml(doc.docType.replace(/_/g, " "))}</td>
                  <td>${doc.verified ? "Approved" : "Pending review"}</td>
                </tr>
              `
            )
            .join("");

    const fundingDetailsMarkup =
      application.category === "jamb"
        ? `
      <div class="section">
        <p class="section-title">JAMB details</p>
        <div class="info-grid">
          <div><strong>Profile Code:</strong> ${escapeHtml(application.profileCode ?? "N/A")}</div>
          <div><strong>JAMB e-pin:</strong> ${escapeHtml(application.jambEPin ?? "Not set yet")}</div>
        </div>
      </div>
      `
        : `
      <div class="section">
        <p class="section-title">Bank details</p>
        <div class="info-grid">
          <div><strong>Bank Name:</strong> ${escapeHtml(application.bankName ?? "N/A")}</div>
          <div><strong>Account Number:</strong> ${escapeHtml(application.bankAccountNumber ?? "N/A")}</div>
          <div><strong>Account Name:</strong> ${escapeHtml(application.bankAccountName ?? "N/A")}</div>
        </div>
      </div>
      `;

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Acknowledgement Slip</title>
    <style>
      @page { size: A4 portrait; margin: 10mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        font-family: Arial, sans-serif;
        color: #111827;
        background: #ffffff;
      }
      .slip {
        max-width: 780px;
        margin: 0 auto;
        border: 1px solid #d1d5db;
        border-radius: 12px;
        padding: 18px;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .header {
        display: flex;
        align-items: center;
        gap: 14px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e5e7eb;
      }
      .logo {
        width: 64px;
        height: 64px;
        border-radius: 999px;
        object-fit: cover;
        flex-shrink: 0;
      }
      .passport {
        width: 72px;
        height: 88px;
        border-radius: 8px;
        object-fit: cover;
        border: 1px solid #d1d5db;
        margin-left: auto;
      }
      .title-top {
        text-transform: uppercase;
        letter-spacing: 0.12em;
        font-size: 11px;
        color: #4b5563;
        margin: 0 0 4px 0;
      }
      .title-main {
        margin: 0;
        font-size: 20px;
      }
      .section {
        margin-top: 14px;
      }
      .section-title {
        margin: 0 0 8px 0;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: #4b5563;
      }
      .info-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px 14px;
        font-size: 13px;
      }
      hr {
        border: none;
        border-top: 1px solid #e5e7eb;
        margin: 14px 0;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
      }
      th, td {
        text-align: left;
        border: 1px solid #e5e7eb;
        padding: 8px 10px;
      }
      th { background: #f9fafb; }
      .muted { color: #6b7280; font-size: 13px; margin: 0; }
      .footer {
        margin-top: 16px;
        padding-top: 10px;
        border-top: 1px dashed #d1d5db;
        font-size: 11px;
        color: #6b7280;
        display: flex;
        justify-content: space-between;
        gap: 10px;
        flex-wrap: wrap;
      }
      @media print {
        html, body { height: auto; }
        .slip { margin: 0; }
      }
    </style>
  </head>
  <body>
    <section class="slip">
      <header class="header">
        <img src="${API_TARGET}/assets/mhj/logo.jpeg" alt="Jantabo Scholarship Initiative logo" class="logo" />
        <div>
          <p class="title-top">Jantabo Scholarship Initiative</p>
          <h1 class="title-main">Acknowledgement Slip</h1>
        </div>
        ${
          passportDocument?.url
            ? `<img src="${passportDocument.url}" alt="Candidate passport" class="passport" />`
            : ""
        }
      </header>

      <div class="section">
        <p class="section-title">Candidate details</p>
        <div class="info-grid">
          <div><strong>Candidate:</strong> ${escapeHtml(user?.fullName ?? "N/A")}</div>
          <div><strong>Email:</strong> ${escapeHtml(user?.email ?? "N/A")}</div>
          <div><strong>Phone:</strong> ${escapeHtml(user?.phone ?? "N/A")}</div>
          <div><strong>Application ID:</strong> ${escapeHtml(application.id)}</div>
          <div><strong>Category:</strong> ${escapeHtml(CATEGORY_LABELS[application.category])}</div>
          <div><strong>Status:</strong> ${escapeHtml(application.status)}</div>
          <div><strong>LGA:</strong> ${escapeHtml(application.lga)}</div>
          <div><strong>State:</strong> ${escapeHtml(application.state)}</div>
          <div><strong>Submitted At:</strong> ${escapeHtml(
            new Date(application.submittedAt).toLocaleString("en-NG")
          )}</div>
        </div>
      </div>

      <hr />

      ${fundingDetailsMarkup}

      <div class="section">
        <p class="section-title">Submitted documents</p>
        ${
          application.documents.length === 0
            ? docsMarkup
            : `<table>
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Approval Status</th>
                  </tr>
                </thead>
                <tbody>${docsMarkup}</tbody>
              </table>`
        }
      </div>
      <footer class="footer">
        <span>Generated from Jantabo Scholarship Initiative Portal</span>
        <span>Application ID: ${escapeHtml(application.id)}</span>
        <span>Generated at: ${escapeHtml(new Date().toLocaleString("en-NG"))}</span>
      </footer>
    </section>
  </body>
</html>`;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const cleanup = () => {
      window.setTimeout(() => {
        iframe.remove();
      }, 1200);
    };

    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) {
      toast.error("Unable to prepare print document.");
      iframe.remove();
      return;
    }

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    const doPrint = () => {
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        toast.error("Unable to start printing.");
        iframe.remove();
        return;
      }
      frameWindow.focus();
      frameWindow.print();
      cleanup();
    };

    iframe.onload = () => {
      window.setTimeout(doPrint, 350);
    };
    window.setTimeout(doPrint, 700);
  };

  const startMutation = useMutation({
    mutationFn: (values: DraftForm) =>
      apiRequest<{ application: ScholarshipApplication }>("/scholarships/start", {
        method: "POST",
        data: {
          category: values.category,
          lga: values.lga,
          state: values.state,
          profileCode: values.category === "jamb" ? values.profileCode || null : null,
          bankName: values.bankName || null,
          bankAccountNumber: values.bankAccountNumber || null,
          bankAccountName: values.bankAccountName || null,
        },
        withCsrf: true,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(["scholarship", "me", user?.id ?? "anonymous"], {
        application: response.application,
      });
      setApplication(response.application);
      toast.success("Draft application saved.");
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ docType, file }: { docType: string; file: File }) => {
      const formData = new FormData();
      formData.append("applicationId", application!.id);
      formData.append("docType", docType);
      formData.append("file", file);
      return apiRequest<{ application: ScholarshipApplication }>(
        "/scholarships/upload",
        {
          method: "POST",
          data: formData,
          withCsrf: true,
        }
      );
    },
    onSuccess: (response) => {
      queryClient.setQueryData(["scholarship", "me", user?.id ?? "anonymous"], {
        application: response.application,
      });
      setApplication(response.application);
      toast.success("Upload completed.");
    },
  });

  const submitMutation = useMutation({
    mutationFn: ({ applicationId }: { applicationId: string }) =>
      apiRequest<{ application: ScholarshipApplication }>("/scholarships/submit", {
        method: "POST",
        data: { applicationId },
        withCsrf: true,
      }),
    onSuccess: (response) => {
      queryClient.setQueryData(["scholarship", "me", user?.id ?? "anonymous"], {
        application: response.application,
      });
      setApplication(response.application);
      toast.success("Application submitted successfully.");
    },
  });

  if (isLoading) {
    return (
      <div className="glass-card rounded-3xl p-6">
        <p className="text-sm text-black/50">Loading application…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {enabledCategories.length === 0 && !application && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Applications are currently closed. Check back later for updates.
        </div>
      )}
      <div className="glass-card rounded-3xl p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary-600">
              Application setup
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
              {application ? "Update your draft" : "Start your application"}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Drafts are saved. Submissions cannot be edited once submitted.
            </p>
            {configData?.allowedLgas != null && configData.allowedLgas.length > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                Applications are open only for selected LGAs.
              </p>
            )}
            <p className="mt-1 text-xs text-slate-500">{uploadSizeHint}</p>
          </div>
          <div className="shrink-0 flex flex-col items-center text-center">
            <label
              className={`relative h-24 w-24 overflow-hidden rounded-full border-2 border-primary-300 bg-primary-50 text-primary-700 transition ${
                !application || !!application.submittedAt || uploadMutation.isPending
                  ? "cursor-not-allowed opacity-60"
                  : "cursor-pointer hover:border-primary-500"
              }`}
              aria-label="Upload passport photo/document"
            >
              {passportDocument?.url ? (
                <img
                  src={passportDocument.url}
                  alt="Uploaded passport preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold uppercase tracking-[0.15em]">
                  Upload
                </span>
              )}
              <input
                type="file"
                accept="image/jpeg,image/png"
                disabled={!application || !!application.submittedAt || uploadMutation.isPending}
                onChange={(event) =>
                  void handleUpload("passport", event.target.files?.[0] ?? null)
                }
                className="sr-only"
              />
            </label>
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-slate-600">
              Passport
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              {passportDocument
                ? `Verified: ${passportDocument.verified ? "Yes" : "Pending"}`
                : "Not uploaded yet"}
            </p>
          </div>
        </div>
        <form onSubmit={handleSubmit(handleStart)} className="mt-6 space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Scholarship category">
              <select
                {...register("category", { required: "Category is required" })}
                disabled={!!application || enabledCategories.length === 0}
                className="rounded-2xl border border-black/15 bg-transparent px-4 py-3 text-black focus:border-black/50 focus:outline-none disabled:opacity-60"
              >
                {enabledCategories.length === 0 ? (
                  <option value="">No categories open</option>
                ) : (
                  enabledCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {CATEGORY_LABELS[cat as ScholarshipCategory]}
                    </option>
                  ))
                )}
              </select>
            </FormField>
            <FormField label="Local Government Area">
              <select
                {...register("lga", { required: "LGA is required" })}
                disabled={allowedLgas.length === 0}
                className="rounded-2xl border border-black/15 bg-transparent px-4 py-3 text-black focus:border-black/50 focus:outline-none"
              >
                <option value="">Select LGA</option>
                {allowedLgas.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
          {category === "jamb" ? (
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="JAMB profile code">
                <input
                  type="text"
                  {...register("profileCode", { required: "Profile code is required" })}
                  placeholder="e.g. 12345678AB"
                  className="rounded-2xl border border-black/15 bg-transparent px-4 py-3 text-black focus:border-black/50 focus:outline-none"
                />
              </FormField>
              <FormField label="JAMB e-pin">
                <input
                  type="text"
                  {...register("jambEPin")}
                  readOnly
                  placeholder="Will be populated after verification"
                  className="rounded-2xl border border-black/15 bg-slate-50 px-4 py-3 text-black/80 focus:outline-none"
                />
              </FormField>
            </div>
          ) : null}
          {errors.profileCode ? (
            <p className="text-xs text-red-500">{errors.profileCode.message}</p>
          ) : null}
          {errors.lga ? (
            <p className="text-xs text-red-500">{errors.lga.message}</p>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="State">
              <input
                type="text"
                {...register("state", { required: "State is required" })}
                disabled
                className="rounded-2xl border border-black/15 bg-transparent px-4 py-3 text-black focus:border-black/50 focus:outline-none"
              />
            </FormField>
            {category !== "jamb" ? (
              <FormField label="Bank name">
                <input
                  type="text"
                  {...register("bankName")}
                  className="rounded-2xl border border-black/15 bg-transparent px-4 py-3 text-black focus:border-black/50 focus:outline-none"
                />
              </FormField>
            ) : null}
          </div>
          {errors.state ? (
            <p className="text-xs text-red-500">{errors.state.message}</p>
          ) : null}
          {category !== "jamb" ? (
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Account number">
                <input
                  type="text"
                  {...register("bankAccountNumber")}
                  className="rounded-2xl border border-black/15 bg-transparent px-4 py-3 text-black focus:border-black/50 focus:outline-none"
                />
              </FormField>
              <FormField label="Account name">
                <input
                  type="text"
                  {...register("bankAccountName")}
                  className="rounded-2xl border border-black/15 bg-transparent px-4 py-3 text-black focus:border-black/50 focus:outline-none"
                />
              </FormField>
            </div>
          ) : null}
          <button
            type="submit"
            disabled={
              startMutation.isPending ||
              (enabledCategories.length === 0 && !application)
            }
            className="rounded-full border border-blue-500 bg-blue-400 px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-500 hover:border-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200 disabled:text-blue-500 disabled:border-blue-200"
          >
            {startMutation.isPending ? "Saving..." : application ? "Save updates" : "Save draft"}
          </button>
        </form>
      </div>
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-black/50">
          Required uploads
        </p>
        <h3 className="mt-3 text-xl font-semibold text-black/50">
          {CATEGORY_LABELS[category]} documents
        </h3>
        <p className="mt-2 text-sm text-black/50">
          Upload all required documents before submitting. Accepted formats: JPG,
          PNG, PDF.
        </p>
        <p className="mt-1 text-xs text-black/50">{uploadSizeHint}</p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {requiredUploadDocs.map((docType) => {
            const existing = application?.documents.find(
              (doc) => doc.docType === docType
            );
            return (
              <div
                key={docType}
                className="rounded-2xl border border-black/15 bg-transparent p-4"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-black/50">
                  {docType.replace(/_/g, " ")}
                </p>
                {existing ? (
                  <div className="mt-3 text-xs text-black/50">
                    <p className="text-emerald-500">Uploaded</p>
                    <p className="mt-1 text-black/50">
                      Verified: {existing.verified ? "Yes" : "Pending"}
                    </p>
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-black/50">Not uploaded yet</p>
                )}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  disabled={!application || !!application.submittedAt || uploadMutation.isPending}
                  onChange={(event) =>
                    void handleUpload(docType, event.target.files?.[0] ?? null)
                  }
                  className="mt-4 block w-full text-xs text-black/50 file:mr-4 file:rounded-full file:border-0 file:bg-slate-500 file:px-4 file:py-2 file:text-xs file:font-semibold file:uppercase file:tracking-[0.2em] file:text-white hover:file:bg-slate-400 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <p className="mt-2 text-[11px] text-black/50">{uploadSizeHint}</p>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="glass-card rounded-3xl p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-black/50">
          Final submission
        </p>
        <h3 className="mt-3 text-xl font-semibold text-black/50">
          Submit for review
        </h3>
        <p className="mt-2 text-sm text-black/50">
          You can only submit once. After submission, edits are locked.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => void handleSubmitApplication()}
            disabled={
              !application ||
              application.submittedAt !== null ||
              missingDocs.length > 0 ||
              submitMutation.isPending
            }
            className="rounded-full border border-blue-500 bg-blue-400 px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-500 hover:border-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200 disabled:text-blue-500 disabled:border-blue-200"
          >
            {submitMutation.isPending ? "Submitting..." : "Submit application"}
          </button>
          {missingDocs.length > 0 ? (
            <p className="text-xs text-black/50">
              Missing: {missingDocs.join(", ").replace(/_/g, " ")}
            </p>
          ) : null}
          {application?.submittedAt ? (
            <>
              <p className="text-xs text-emerald-500">
                Submitted. You will receive updates by email and SMS.
              </p>
              <button
                type="button"
                onClick={handlePrintAcknowledgement}
                className="no-print rounded-full border border-black/20 bg-white px-6 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-black transition hover:border-black/40"
              >
                Print acknowledgement
              </button>
            </>
          ) : null}
        </div>
      </div>
      {application?.submittedAt ? (
        <section
          id="ack-slip"
          className="rounded-3xl border border-black/15 bg-white p-6 text-black shadow-sm"
        >
          <header className="flex items-center gap-4 border-b border-black/10 pb-4">
            <img
              src={`${API_TARGET}/assets/mhj/logo.jpeg`}
              alt="Jantabo Scholarship Initiative logo"
              className="h-16 w-16 rounded-full object-cover"
            />
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-black/60">
                Jantabo Scholarship Initiative
              </p>
              <h4 className="mt-1 text-lg font-semibold">
                Acknowledgement Slip
              </h4>
            </div>
          </header>
          <div className="mt-5 grid gap-3 text-sm md:grid-cols-2">
            <p>
              <span className="font-semibold">Candidate:</span>{" "}
              {user?.fullName ?? "N/A"}
            </p>
            <p>
              <span className="font-semibold">Email:</span>{" "}
              {user?.email ?? "N/A"}
            </p>
            <p>
              <span className="font-semibold">Phone:</span>{" "}
              {user?.phone ?? "N/A"}
            </p>
            <p>
              <span className="font-semibold">Application ID:</span>{" "}
              {application.id}
            </p>
            <p>
              <span className="font-semibold">Category:</span>{" "}
              {CATEGORY_LABELS[application.category]}
            </p>
            <p>
              <span className="font-semibold">Status:</span>{" "}
              {application.status}
            </p>
            <p>
              <span className="font-semibold">LGA:</span>{" "}
              {application.lga}
            </p>
            <p>
              <span className="font-semibold">State:</span>{" "}
              {application.state}
            </p>
            <p>
              <span className="font-semibold">Submitted At:</span>{" "}
              {new Date(application.submittedAt).toLocaleString("en-NG")}
            </p>
          </div>
          <div className="mt-6 border-t border-black/10 pt-4">
            <p className="text-xs uppercase tracking-[0.2em] text-black/60">
              Submitted documents
            </p>
            <div className="mt-3 space-y-2 text-sm">
              {application.documents.length === 0 ? (
                <p className="text-black/70">No documents found.</p>
              ) : (
                application.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between rounded-xl border border-black/10 px-3 py-2"
                  >
                    <span className="capitalize text-black/90">
                      {doc.docType.replace(/_/g, " ")}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] uppercase tracking-[0.15em] ${
                        doc.verified
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {doc.verified ? "Approved" : "Pending review"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
